import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Brain,
  Building2,
  Check,
  Copy,
  ExternalLink,
  Globe,
  Settings2,
  Users as UsersIcon,
  Workflow,
} from "lucide-react";
import { toast } from "sonner";
import {
  getNativeClientPlatform,
  updateNativeClientPlatform,
} from "../../../services/native-client-platform";
import { TabsList } from "../../../components/ui/tabs";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import Loading from "../../../components/loading";
import { ImageUpload } from "../../../components/ui/image-upload";
import GeneralForm from "./general-form";
import AgentsTab from "./agents-tab";
import UsersTab from "./users-tab";

const TABS = [
  { label: "Agentes", key: "agents", icon: Brain },
  { label: "Geral", key: "general", icon: Settings2 },
  { label: "Usuários", key: "users", icon: UsersIcon },
];

export default function EditClientPlatform() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadPlatform = useCallback(() => {
    setLoading(true);
    getNativeClientPlatform(id)
      .then(({ data: platform }) => {
        setData(platform);
        document.title = "Verboo | " + platform?.name;
      })
      .catch((e) => {
        console.error(e);
        toast.error("Falha ao carregar cliente");
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    loadPlatform();
    return () => { document.title = "Verboo"; };
  }, [loadPlatform]);

  const selectedTab = useMemo(() => {
    const segments = location.pathname.split("/");
    return segments[segments.length - 1] || "general";
  }, [location.pathname]);

  const onTabChange = useCallback(
    (key) => navigate(`/client-platforms/${id}/${key}`),
    [navigate, id]
  );

  if (loading) return <Loading title="Carregando..." />;
  if (!data) return <Loading title="Cliente não encontrado" />;

  const url = data.customURL || (data.subdomain ? `${data.subdomain}.verboo.com.br` : null);

  return (
    <div className="dash-scroll flex h-full w-full flex-col overflow-y-auto bg-[var(--dash-bg)]">
      <PlatformHeader data={data} url={url} onLogoChange={setData} />

      <div className="sticky top-0 z-30 border-b border-[var(--dash-border)] bg-[var(--dash-bg)]">
        <div className="mx-auto w-full max-w-[1400px] px-6">
          <TabsList items={TABS} value={selectedTab} onChange={onTabChange} />
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1400px] flex-1 px-6 py-6">
        <Routes>
          <Route index element={<Navigate to="agents" replace />} />
          <Route path="general" element={<GeneralForm data={data} setData={setData} />} />
          <Route path="agents" element={<AgentsTab platformId={data.id} companyId={data.companyID} />} />
          <Route path="users" element={<UsersTab platformId={data.id} />} />
          <Route path="*" element={<Navigate to="general" replace />} />
        </Routes>
      </div>
    </div>
  );
}

function PlatformHeader({ data, url, onLogoChange }) {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  function copyId() {
    navigator.clipboard.writeText(String(data.id));
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  }

  const handleLogoUpload = useCallback((logoUrl) => {
    updateNativeClientPlatform(data.id, { ...data, logo: logoUrl || "" })
      .then(({ data: updated }) => {
        onLogoChange?.(updated);
        toast.success(logoUrl ? "Logo atualizada!" : "Logo removida!");
      })
      .catch(() => toast.error("Falha ao salvar logo"));
  }, [data, onLogoChange]);

  return (
    <div className="border-b border-[var(--dash-border)] bg-[var(--dash-surface)]">
      <div className="mx-auto w-full max-w-[1400px] px-6 py-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <ImageUpload
              value={data.logo || null}
              onChange={handleLogoUpload}
              shape="rounded"
              size="md"
              placeholder="Logo"
            />
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-semibold tracking-tight text-[var(--dash-text)]">{data.name}</h1>
                <Badge variant={data.isActive !== false ? "success" : "muted"} dot>
                  {data.isActive !== false ? "Ativo" : "Inativo"}
                </Badge>
                {data.nativeClientPlatformPlan?.name && <Badge variant="primary">{data.nativeClientPlatformPlan.name}</Badge>}
              </div>
              <div className="flex items-center gap-3">
                <button type="button" onClick={copyId} className="group flex w-fit items-center gap-1.5 rounded-md bg-[var(--dash-surface-subtle)] px-2 py-0.5 font-mono text-[11px] text-[var(--dash-text-muted)] ring-1 ring-[var(--dash-border)]">
                  <span>ID: {data.id}</span>
                  {copied ? <Check size={11} className="text-[var(--dash-primary)]" /> : <Copy size={11} />}
                </button>
                {url && (
                  <a href={data.customURL || `https://${url}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[11px] font-medium text-[var(--dash-primary)] hover:opacity-80">
                    <Globe size={11} />{url}<ExternalLink size={9} />
                  </a>
                )}
              </div>
            </div>
          </div>
          <Button
            variant="primary"
            leftIcon={Brain}
            onClick={() => navigate(`/onboarding?flow=agent&clientId=${data.id}`)}
          >
            Criar agente
          </Button>
        </div>
      </div>
    </div>
  );
}
